import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { NotificationsNoneRounded } from "@mui/icons-material";
import { fetchById } from "../helpers/actions";
import { activityFeedUrl } from "../helpers/urls";
import { getDecodedToken } from "../helpers/auth";

/* Top-bar bell: live activity across the whole admin (bookings, payments, leads, therapists, reviews, Noida,
   assessments, plans, payouts, internships, collaborations, workshops, new clients). Checks every 15 s while
   the tab is open; new items pop up as a toast. "Read" is remembered per admin on this device. */
const FILTERS = [
  ["all", "All"], ["payment", "Payments"], ["booking", "Bookings"], ["lead", "Leads"], ["therapist", "Therapists"],
  ["noida", "Noida"], ["intern", "Interns"], ["client", "Clients"],
];
const GROUP = { payment: "payment", payout: "payment", plan: "payment", booking: "booking", session: "booking", workshop: "booking", lead: "lead", therapist: "therapist", assessment: "therapist", review: "therapist", noida: "noida", urgent: "noida", collab: "noida", intern: "intern", client: "client" };
const ICON = { payment: "₹", payout: "₹", plan: "★", booking: "📅", session: "✓", workshop: "🎓", lead: "☎", therapist: "🧠", assessment: "📋", review: "★", noida: "🏥", urgent: "⚡", collab: "🤝", intern: "🎓", client: "👤" };

function ago(d) {
  const m = Math.max(0, Math.round((Date.now() - new Date(d).getTime()) / 60000));
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short" });
}

export default function AdminBell({ canSendPage }) {
  const navigate = useNavigate();
  const uid = getDecodedToken()?.userId || "me";
  const SEEN = `cyt_bell_seen_${uid}`;
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState("all");
  const [seen, setSeen] = useState(() => { try { return localStorage.getItem(SEEN) || ""; } catch { return ""; } });
  const lastNow = useRef(null);
  const known = useRef(new Set());
  const first = useRef(true);
  const wrap = useRef(null);

  const poll = useCallback(async () => {
    try {
      const since = lastNow.current ? new Date(new Date(lastNow.current).getTime() - 120000).toISOString() : undefined;
      const r = await fetchById(activityFeedUrl, since ? { since } : {});
      if (!r?.status) return;
      lastNow.current = r.now;
      const fresh = (r.data || []).filter((i) => !known.current.has(i.id));
      fresh.forEach((i) => known.current.add(i.id));
      if (fresh.length) {
        setItems((cur) => [...fresh, ...cur].sort((a, b) => new Date(b.at) - new Date(a.at)).slice(0, 200));
        if (!first.current) {
          const show = fresh.slice(0, 3);
          show.forEach((i) => toast.info(<div className="ab-toast"><b>{i.title}</b><span>{i.body}</span></div>, { onClick: () => navigate(i.link), autoClose: 7000, icon: false }));
          if (fresh.length > 3) toast.info(`${fresh.length - 3} more new updates — open the bell`, { autoClose: 5000 });
        }
      }
      first.current = false;
    } catch { /* offline / no access — try again next time */ }
  }, [navigate]);

  useEffect(() => {
    poll();
    const iv = setInterval(() => { if (document.visibilityState === "visible") poll(); }, 15000);
    const vis = () => { if (document.visibilityState === "visible") poll(); };
    document.addEventListener("visibilitychange", vis);
    return () => { clearInterval(iv); document.removeEventListener("visibilitychange", vis); };
  }, [poll]);
  useEffect(() => {
    if (!open) return undefined;
    const off = (e) => { if (wrap.current && !wrap.current.contains(e.target)) setOpen(false); };
    const esc = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", off);
    document.addEventListener("keydown", esc);
    return () => { document.removeEventListener("mousedown", off); document.removeEventListener("keydown", esc); };
  }, [open]);

  const isNew = (i) => !seen || new Date(i.at) > new Date(seen);
  const unread = items.filter(isNew).length;
  const markAll = () => { const t = new Date().toISOString(); setSeen(t); try { localStorage.setItem(SEEN, t); } catch { /* ignore */ } };
  const shown = items.filter((i) => filter === "all" || GROUP[i.type] === filter);
  const todayStr = new Date().toDateString();
  const today = shown.filter((i) => new Date(i.at).toDateString() === todayStr);
  const earlier = shown.filter((i) => new Date(i.at).toDateString() !== todayStr);

  const row = (i) => (
    <button key={i.id} type="button" className={`ab-i ${i.tone || ""}${isNew(i) ? " new" : ""}`} onClick={() => { setOpen(false); navigate(i.link); }}>
      <span className="ab-ic" aria-hidden="true">{ICON[i.type] || "•"}</span>
      <span className="ab-t"><b>{i.title}</b><small>{i.body}</small></span>
      <em>{ago(i.at)}</em>
    </button>
  );

  return (
    <div className="ab" ref={wrap}>
      <style>{CSS}</style>
      <button type="button" className="ls-bell ab-btn" onClick={() => setOpen((o) => !o)} title="Notifications" aria-label={`Notifications${unread ? ` (${unread} new)` : ""}`} aria-expanded={open}>
        <NotificationsNoneRounded />
        {unread > 0 && <span className="ab-n">{unread > 99 ? "99+" : unread}</span>}
      </button>
      {open && (
        <div className="ab-pop" role="dialog" aria-label="Notifications">
          <div className="ab-h">
            <b>Notifications</b>
            {unread > 0 && <span className="ab-cnt">{unread} new</span>}
            <button type="button" className="ab-mark" onClick={markAll} disabled={!unread}>Mark all read</button>
          </div>
          <div className="ab-f">
            {FILTERS.map(([k, l]) => {
              const n = k === "all" ? items.filter(isNew).length : items.filter((i) => GROUP[i.type] === k && isNew(i)).length;
              return <button key={k} type="button" className={filter === k ? "on" : ""} onClick={() => setFilter(k)}>{l}{n ? <i>{n}</i> : null}</button>;
            })}
          </div>
          <div className="ab-list">
            {shown.length === 0 ? <div className="ab-empty">Nothing here in the last 7 days</div> : (
              <>
                {today.length > 0 && <div className="ab-g">Today</div>}
                {today.map(row)}
                {earlier.length > 0 && <div className="ab-g">Earlier</div>}
                {earlier.map(row)}
              </>
            )}
          </div>
          {canSendPage && <Link to="/notifications" className="ab-foot" onClick={() => setOpen(false)}>Send a notification to users →</Link>}
        </div>
      )}
    </div>
  );
}

const CSS = `
.ab { position: relative; }
.ab-btn { position: relative; border: 0; background: none; cursor: pointer; display: grid; place-items: center; }
.ab-n { position: absolute; top: 3px; right: 2px; min-width: 18px; height: 18px; padding: 0 5px; box-sizing: border-box; border-radius: 9px; background: #ef4444; color: #fff; font-size: 10.5px; font-weight: 800; display: grid; place-items: center; border: 2px solid #14532d; line-height: 1; }
.ab-pop { position: absolute; right: -8px; top: calc(100% + 10px); width: 400px; max-width: calc(100vw - 16px); background: #fff; border: 1px solid #e2e8e4; border-radius: 16px; box-shadow: 0 24px 60px rgba(15,23,42,.22); z-index: 1400; overflow: hidden; color: #0f172a; animation: abIn .16s ease; }
@keyframes abIn { from { opacity: 0; transform: translateY(-6px); } to { opacity: 1; transform: none; } }
.ab-h { display: flex; align-items: center; gap: 8px; padding: 14px 16px 10px; }
.ab-h b { font-size: 15.5px; font-weight: 800; color: #14532d; }
.ab-cnt { font-size: 11px; font-weight: 800; color: #b91c1c; background: #fef2f2; border-radius: 999px; padding: 2px 8px; }
.ab-mark { margin-left: auto; border: 0; background: none; font: inherit; font-size: 12.5px; font-weight: 800; color: #15803d; cursor: pointer; }
.ab-mark:disabled { color: #b0bac4; cursor: default; }
.ab-f { display: flex; gap: 5px; overflow-x: auto; padding: 0 12px 10px; border-bottom: 1px solid #eef2f0; scrollbar-width: none; }
.ab-f::-webkit-scrollbar { display: none; }
.ab-f button { flex-shrink: 0; height: 28px; padding: 0 10px; border-radius: 999px; border: 1px solid #e2e8e4; background: #fff; font: inherit; font-size: 12px; font-weight: 700; color: #475569; cursor: pointer; display: inline-flex; align-items: center; gap: 5px; }
.ab-f button.on { background: #14532d; border-color: #14532d; color: #fff; }
.ab-f i { font-style: normal; font-size: 10px; font-weight: 800; background: #ef4444; color: #fff; border-radius: 999px; padding: 0 5px; }
.ab-list { max-height: min(460px, 60vh); overflow-y: auto; padding: 4px 6px 8px; }
.ab-g { font-size: 10.5px; font-weight: 800; text-transform: uppercase; letter-spacing: .06em; color: #94a3b8; padding: 10px 10px 4px; }
.ab-i { width: 100%; display: flex; align-items: flex-start; gap: 10px; padding: 9px 10px; border: 0; border-radius: 12px; background: none; font: inherit; text-align: left; cursor: pointer; position: relative; }
.ab-i:hover { background: #f6faf7; }
.ab-i.new { background: #f3faf5; }
.ab-i.new::after { content: ""; position: absolute; top: 14px; right: 10px; width: 8px; height: 8px; border-radius: 50%; background: #16a34a; }
.ab-ic { width: 34px; height: 34px; border-radius: 10px; flex-shrink: 0; display: grid; place-items: center; font-size: 15px; font-weight: 800; background: #f1f5f3; color: #334155; }
.ab-i.green .ab-ic { background: #dcfce7; color: #166534; }
.ab-i.amber .ab-ic { background: #fef3c7; color: #92400e; }
.ab-i.red .ab-ic { background: #fee2e2; color: #b91c1c; }
.ab-i.blue .ab-ic { background: #dbeafe; color: #1d4ed8; }
.ab-i.violet .ab-ic { background: #ede9fe; color: #5b21b6; }
.ab-i.teal .ab-ic { background: #ccfbf1; color: #0f766e; }
.ab-i.gold .ab-ic { background: #fbefc8; color: #8a5a0b; }
.ab-t { display: flex; flex-direction: column; min-width: 0; flex: 1; padding-right: 14px; }
.ab-t b { font-size: 13px; font-weight: 800; color: #0f172a; line-height: 1.3; }
.ab-t small { font-size: 12px; color: #64748b; line-height: 1.4; overflow: hidden; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; }
.ab-i em { font-style: normal; font-size: 11px; color: #94a3b8; white-space: nowrap; font-weight: 600; margin-top: 1px; }
.ab-i.new em { margin-right: 14px; }
.ab-empty { padding: 34px 10px; text-align: center; color: #94a3b8; font-size: 13px; }
.ab-foot { display: block; padding: 11px 16px; border-top: 1px solid #eef2f0; font-size: 12.5px; font-weight: 800; color: #15803d !important; text-decoration: none !important; background: #fafcfb; }
.ab-toast { display: flex; flex-direction: column; gap: 2px; }
.ab-toast b { font-size: 13px; }
.ab-toast span { font-size: 12px; opacity: .85; }
@media (max-width: 767px), (max-width: 1023px) and (orientation: portrait) {
  .ab-pop { position: fixed; left: 8px; right: 8px; top: calc(var(--top-h, 56px) + 6px); width: auto; max-width: none; }
}
`;
